import './toast.scss'
import clsx from 'clsx'
import { CSSProperties, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { GreenSwooshIcon, GreenSwooshSVGIcon } from '@/shared/assets'

interface ToastProps {
    message: string
    visible: boolean
    onClose: () => void
    greenSwooshIcon?: boolean
    icon?: React.ReactNode
    timeout?: number
    className?: string
    style?: CSSProperties
}

export const Toast = ({
    message,
    visible,
    onClose,
    greenSwooshIcon,
    icon = <GreenSwooshSVGIcon />,
    timeout = 3000,
    className,
    style,
}: ToastProps) => {
    // Используем useRef для отслеживания активного таймера
    // Это предотвращает создание нескольких таймеров в Safari из-за особенностей рендеринга
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    // Флаг для предотвращения двойного вызова onClose в Safari
    const isClosingRef = useRef(false)

    useEffect(() => {
        // Очищаем предыдущий таймер при изменении visible или timeout
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        
        // Сбрасываем флаг закрытия при показе нового уведомления
        if (visible) {
            isClosingRef.current = false
        }

        if (visible) {
            timerRef.current = setTimeout(() => {
                // Защита от двойного вызова onClose в Safari
                if (!isClosingRef.current) {
                    isClosingRef.current = true
                    onClose()
                }
                timerRef.current = null
            }, timeout)

            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current)
                    timerRef.current = null
                }
            }
        }
    }, [visible, onClose, timeout])

    return (
        <>
            {createPortal(
                <div
                    className={clsx(
                        'toast',
                        {
                            'toast--visible': visible,
                            'toast--hidden': !visible,
                        },
                        className
                    )}
                    style={style}
                >
                    {icon
                        ? icon
                        : greenSwooshIcon && (
                              <img
                                  src={GreenSwooshIcon}
                                  className="toast__icon"
                                  alt="Success"
                              />
                          )}
                    {message}
                </div>,
                document.body
            )}
        </>
    )
}
